import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { FunctionsService } from '../utilities/services/functions.service';
import { FileUploadModel } from '../utilities/models/file-upload-model';
import { LocaleService } from '@app/system/services/locale.service';
import { BuilderService } from '../utilities/services/builder.service';
import { BaseService } from '@app/system/services/base.service';
import { GlobalFunctionService } from '@app/system/services/global-function.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageCropModel } from '../utilities/models/image-crop-model';
import { HandleBarPipe } from '@app/system/pipes/handle-bar/handle-bar.pipe';

@Component({
	selector: 'app-file-upload',
	templateUrl: './file-upload.component.html',
	styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit {

	/**
	 * @desc Angular Input Decorator - Common Parameters
	 * @param object field - file upload field data.
	 * @param formControl control - file upload control.
	 */
	@Input() field: FileUploadModel;
	@Input() control: UntypedFormControl;
	@Input() progress;
	@Input() multiType: boolean = false;
	@ViewChild('thumbnailUpload') thumbnailUpload: HTMLInputElement;

	validator: any;
	videoThumbnail: any;
	coverImageLoader = false;
	cropper: ImageCropModel;
	cropperControl: UntypedFormControl;
	thumbanilCropped = false;
	inValidThumbnail: any = null;
	helpText: any = {
		size: '',
		type: ''
	};
	state = true;

	fileIcon: any = 'assets/img/dummy-img.png';

	/**
	 * @desc Angular Output Decorator - Common Parameters
	 * @param object helpLineEmit - help line json data.
	 */
	@Output() helpLineEmit = new EventEmitter();
	constructor(
		private host: ElementRef<HTMLInputElement>,
		public locale: LocaleService,
		public controlFunctions: FunctionsService,
		private builderService: BuilderService,
		private handleBarPipe: HandleBarPipe,
		private baseService: BaseService,
		public gfService: GlobalFunctionService,
		private sanitizer: DomSanitizer
	) { }

	ngOnInit() {
		let size = this.field.additionalMetaData.fileSize.toString().replace(/ /g, '').replace(/[^0-9\.]/g, '').trim();
		if (this.field.additionalMetaData.fileSize.toString().includes('MB')) {
			size += ' MB';
		} else if (this.field.additionalMetaData.fileSize.toString().includes('KB')) {
			size += ' KB';
		} else {
			size += ' GB';
		}
		this.helpText.size = size;
		if (this.field.additionalMetaData.supportedType !== null) {
			this.helpText.type = this.field.additionalMetaData.supportedType.toString().split('.').join(' ').toUpperCase();
		}
		if (this.field.additionalMetaData.drag && this.field.isRequired && !this.control.value) {
			this.control.setErrors(({ required: true }));
		}
	}

	ngOnChanges(changes) {
		if (
			changes.hasOwnProperty('field') &&
			changes.field.currentValue && changes.field.currentValue.value &&
			typeof changes.field.currentValue.value === 'object'
		) {

			if (changes.hasOwnProperty('multiType')) {
				if (changes.multiType.currentValue) {
					this.control.patchValue(changes.field.currentValue.value, { emitEvent: true });
				}
			}

		}
	}

	triggerFile(input: HTMLInputElement) {
		if (navigator.onLine) {
			this.control.markAsTouched();
			if (this.field.isRequired) {
				this.control.setErrors(({ required: true }));
			}
			input.click();
		}
	}

	resetFile(input: HTMLInputElement) {
		this.field.value = null;
		input.value = null;
		this.control.patchValue(null);
		this.fileIcon = null;
		this.field.additionalMetaData.thumbnail = null;
	}

	/**
	 * @desc Help line field change method and emit the data.
	 * @param {json} data help line json data.
	 */
	helpLineData(data) {
		this.helpLineEmit.emit(data);
	}



	async uploadFile(event) {
		if (navigator.onLine) {
			if (this.control.untouched) {
				this.control.markAsTouched();
			}
			if (event.target.files[0]) {
				const file = event.target.files[0];
				const validator = this.control.validator;
				this.control.clearValidators();
				this.control.patchValue(file.name, { emitEvent: false });
				this.control.patchValue(file, { emitModelToViewChange: false });
				this.control.setValidators(validator);
				this.control.updateValueAndValidity();
				this.field.value = file;
				const fileExt = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
				if (['jpg', 'jpeg', 'png'].includes(fileExt)) {
					this.fileIcon = await this.gfService.fileToDataURL(file);
				} else {
					this.fileIcon = this.baseService.getSnapshot(file.name);
				}
			}
		}
	}

	validateThumbnail(thumbnail) {
		const sizeValidator = (file, sizeVal) => {
			if (file.size > Number(sizeVal)) {
				// this.fileTypeError[formField.fieldColumn] = true;
				return {
					file: {
						message: this.handleBarPipe.transform(this.locale.translate('file_size'), {
							size: formatBytes(sizeVal)
						}),
						currentFileSize: file.size,
						actualFileSize: sizeVal
					}
				};
			}
			return null;
		};

		const formatBytes = (bytes: number) => {
			const si = true;
			const thresh = si ? 1000 : 1024;
			if (Math.abs(bytes) < thresh) {
				return bytes + ' BYTES';
			}
			const units = si
				? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
				: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
			let u = -1;
			do {
				bytes /= thresh;
				++u;
			} while (Math.abs(bytes) >= thresh && u < units.length - 1);
			return bytes.toFixed(1) + ' ' + units[u];
		};

		let returnData = null;

		const size = this.field.additionalMetaData.thumbnailMaxSize;
		if (![null, undefined].includes(size)) {
			if (typeof size === 'string' && (size.includes('MB') || size.includes('KB') || size.includes('BYTES'))) {
				const data = Number(size.replace(/ /g, '').replace(/[^0-9\.]/g, '').trim()) || 0;
				if (size.includes('MB')) {
					returnData = sizeValidator(thumbnail, data * 1e6);
				} else if (size.includes('KB')) {
					returnData = sizeValidator(thumbnail, data * 1000);
				} else if (size.includes('GB')) {
					// returnData = sizeValidator(control.value, data * 1e+9);
				} else {
					returnData = sizeValidator(thumbnail, data);
				}
			}
		}

		const thumbFormat = this.field.additionalMetaData.thumbnailFormat;
		if (![null, undefined].includes(thumbFormat)) {
			const fileExt = '.' + thumbnail.type.split('/')[1].toLowerCase();
			if (
				(typeof thumbFormat === 'string' && thumbFormat !== fileExt) ||
				(Array.isArray(thumbFormat) && thumbFormat.length > 0 && !thumbFormat.includes(fileExt))
			) {
				returnData = {
					file: {
						allowedExtension: thumbFormat,
						givenExtension: fileExt,
						message: this.locale.translate('invalid_file_format')
					}
				};
			}
		}

		return returnData;
	}


	async uploadThumbnail($event, file: HTMLInputElement) {
		if (navigator.onLine) {
			this.thumbanilCropped = false;
			this.coverImageLoader = true;
			this.inValidThumbnail = this.validateThumbnail($event.target.files[0]);
			if ($event.target.files[0] && !this.inValidThumbnail) {
				const reader = new FileReader();
				reader.onload = (e) => {
					// this.videoThumbnail = e.target.result;
				};
				const src: any = await this.gfService.fileToDataURL($event);
				this.cropper = {
					fieldColumn: 'thumbnail',
					fieldType: 'imageCrop',
					additionalMetaData: {
						cropBoxResizable: false,
						dragMode: 'move',
						showBackground: true,
						dragType: 'rectangle',
						autoCrop: true,
						zoom: false,
						width: this.field.additionalMetaData.thumbnailWidth || 200,
						height: this.field.additionalMetaData.thumbnailHeight || 100,
						toggleDragModeOnDblclick: false,
						showLabel: false,
						dataUrl: this.field.additionalMetaData.dataUrl,
						showUploadIcon: false
					},
					isHidden: false,
					isReadonly: false,
					isRequired: true,
					value: null,
					fieldHelpText: this.locale.translate('use_mouse_scroll_to_zoom')
				};
				this.cropper.value = {
					imageSrc: src
				};
				this.builderService.fieldModelBuilder(this.cropper);
				this.cropperControl = this.builderService.controlBuilder(this.cropper);
				reader.readAsDataURL($event.target.files[0]);
				this.coverImageLoader = false;
			}
		}

	}
	removeThumbnail() {
		if (navigator.onLine) {
			this.thumbanilCropped = true;

		}
	}

	async submit() {
		if (navigator.onLine) {
			this.videoThumbnail = null;
			this.state = false;
			this.videoThumbnail = this.cropperControl.value.croppedFile;
			if (!this.field.additionalMetaData.dataUrl) {
				this.videoThumbnail = await this.gfService.fileToDataURL(this.cropperControl.value.croppedFile);
			}
			this.thumbanilCropped = true;
			const file = {
				file: this.field,
				thumbnail: this.cropperControl.value.croppedFile
			};
			// if (this.field.additionalMetaData.dataUrl) {
			// 	file.videoFile = this.videoSrc;
			// }

			const validator = this.control.validator;
			this.validator = this.control.validator;
			this.control.clearValidators();
			this.control.patchValue(file, { emitModelToViewChange: false, emitEvent: false });
			this.control.setValidators(validator);
			this.control.updateValueAndValidity();
		}
	}

	changeThumbnail() {
		if (navigator.onLine) {
			this.removeThumbnail();
		}
	}

}
